import { t } from "components/CentralDataMangement/translation/Translation";
import Title from "components/common/atoms/Title";
import OfficeTargetTable from "../common/OfficeTargetTable";
import { CooAgreementDetails, OfficeTarget, ProspectCallDetails, StarterDetails, StopperDetails, User, VacancyDetails, VisitsDetails, WeeklyReportsData } from "../Annotations";
import { useEffect, useMemo, useState } from "react";
import { CircularProgressbarWithChildren } from "react-circular-progressbar";
import WeeklyReportTable from "../common/WeeklyReportTable";
import VacanciesTable from "../common/VacanciesTable";
import VisitsTable from "../common/VisitsTable";
import ProspectCallsTable from "../common/ProspectCallsTable";
import CooAgreementTable from "../common/CooAgreementTable";
import TableTitle from "components/common/atoms/TableTitle";
import StartersTable from "../common/StartersTable";
import StoppersTable from "../common/StoppersTable";
import LabelWithTextAreaField from "components/common/molecules/LabelWithTextAreaField";
import EncryptDecryptService from "components/common/services/EncryptDecryptService";
import CustomNotify from "components/common/atoms/CustomNotify";
import { ApiCall } from "components/common/services/ApiServices";
import { EDIT_WEEKLY_REPORTS, UPDATE_WEEKLY_REPORTS } from "../../../../routes/ApiEndpoints";
import { ADMIN, CENTRAL_DATA_MANAGEMENT_MICROSERVICE, OFFICE_MANAGER, SUPER_ADMIN } from "Constants";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Button from "components/common/atoms/Button";
import LoadingIcon from "components/common/utlis/LoadingIcon";
import AccessControl from "components/common/services/RolesAndPermissions/AccessControl";
import { selectAuth } from "features/auth/AuthSlice";
import { useSelector } from "react-redux";

interface TargetData {
  officeQTarget: OfficeTarget;
  officeTarget: OfficeTarget;
}

interface ReportsData {
  primaryPeriodId: number | string | null;
  weeklyReports: WeeklyReportsData;
  vacancies: VacancyDetails[];
  foreignVacancies: VacancyDetails[];
  visits: VisitsDetails[];
  cooAgreement: CooAgreementDetails[];
  prospectCalls: ProspectCallDetails[];
  starters: StarterDetails[];
  stoppers: StopperDetails[];
  from?: string | null;
  to?: string | null;
  omName?: string;
  /** Per-locale activity wording keyed by follow-up key, supplied by the report API. */
  activityLabels?: Record<string, string>;
}

interface WeeklyData {
  created_by: number | string;
  reports: WeeklyReportsData;
}

interface RecruitersData {
  recruiters: User[];
  recruitersData: WeeklyData[];
  starters: StarterDetails[];
  stoppers: StopperDetails[];
}

interface Analysis {
  challenges: string;
  dubi_management: string;
  office_management: string;
}

const WeeklyReports: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [targetData, setTargetData] = useState<TargetData>();
  const [reports, setReports] = useState<ReportsData>({
    primaryPeriodId: null,
    weeklyReports: {},
    vacancies: [],
    foreignVacancies: [],
    visits: [],
    prospectCalls: [],
    cooAgreement: [],
    starters: [],
    stoppers: [],
  });
  const [recruiters, setRecruiters] = useState<RecruitersData>({
    recruiters: [],
    recruitersData: [],
    starters: [],
    stoppers: []
  });
  const [currentRecData, setCurrentRecData] = useState<WeeklyData>();
  const [analysisData, setAnalysisData] = useState<Analysis>({
    challenges: "",
    dubi_management: "",
    office_management: "",
  });

  // let data = secureLocalStorage.getItem('omWeeklyReport') as string; // Get the 'data' query param as a string
  const [searchParams] = useSearchParams();
  const data = searchParams.get("data");
  const authUser = useSelector(selectAuth);

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchData();
    }, 3000); // 3 seconds

    return () => clearTimeout(timeout); // Cleanup the timeout on component unmount
  }, []);

  const fetchData = async () => {
    let parsedData = {};
    try {
      if (data) {
        // Decrypt the recruiterData
        let decryptedData = decodeURIComponent(EncryptDecryptService.decryptData(data));

        // If decryptedData is in JSON format, parse it
        parsedData = JSON.parse(decryptedData);
      }

    } catch (error) {
      CustomNotify({
        type: "error",
        message: t("Something went wrong"),
      });
      return null;
    }

    const response = await ApiCall.service(
      EDIT_WEEKLY_REPORTS,
      "POST",
      { ...parsedData, type: "officeManager" },
      false,
      CENTRAL_DATA_MANAGEMENT_MICROSERVICE
    );

    if (response?.status === 200) {
      setTargetData(response?.data?.targets);
      setReports({
        primaryPeriodId: response?.data?.primaryPeriodId,
        weeklyReports: response?.data?.weeklyReports,
        vacancies: response?.data?.vacancies,
        foreignVacancies: response?.data?.foreignVacancies,
        visits: response?.data?.visits,
        prospectCalls: response?.data?.prospectCalls,
        cooAgreement: response?.data?.coopAgreements,
        starters: response?.data?.starters,
        stoppers: response?.data?.stoppers,
        from: response?.data?.from,
        to: response?.data?.to,
        omName: response?.data?.omName,
        // Per-locale activity wording from the API; the state object is built field by field,
        // so this has to be carried over explicitly or the rows fall back to the English keys.
        activityLabels: response?.data?.activityLabels
      });
      setRecruiters(response?.data?.recruiterData);
      setCurrentRecData({ created_by: "global", reports: {} });
      setAnalysisData((prevData) => ({ ...prevData, ...response?.data?.analysis }));
    } else {
      CustomNotify({
        type: "error",
        message: t("Some error occured")
      });
    }
    setLoading(false);
  }
  const foreignOffice = targetData?.officeQTarget?.isForeign

  // const rows = useMemo(() => [
  //   { key: 'visits', label: 'Visits' },
  //   { key: 'workplace_visit', label: 'Workplace visits' },
  //   { key: 'vacancies', label: 'Vacancies' },
  //   { key: 'vacancies_fro', label: 'Vacancies FRO' },
  //   { key: 'prospect_calls', label: 'Prospect call' },
  //   { key: 'starter', label: 'Starter' },
  //   { key: 'stopper', label: 'Stopper' },
  //   { key: 'stopper_permanent', label: 'Stopper permanent' },
  // ], []);

  const rows = useMemo(() => {
  const baseRows = [
    { key: 'visits', label: 'Visits' },
    { key: 'workplace_visit', label: 'Workplace visits' },
    { key: 'vacancies', label: 'Vacancies' },
    { key: 'vacancies_fro', label: 'Vacancies FRO' },
    { key: 'prospect_calls', label: 'Prospect call' },
    { key: 'starter', label: 'Starter' },
    { key: 'stopper', label: 'Stopper' },
    { key: 'stopper_permanent', label: 'Stopper permanent' },
  ];

  return foreignOffice
    ? baseRows.filter(row => row.key !== 'vacancies')
    : baseRows;
}, [foreignOffice]);


  const setCurrentRecruiter = (id: number | string) => {
    const recruiter = recruiters.recruitersData?.find(data => data.created_by === id);
    setCurrentRecData(recruiter);
  }

  const getStarters = (id: number | string) => {
    return recruiters.starters.filter(data => data.created_by === id);
  }

  const getStoppers = (id: number | string) => {
    return recruiters.stoppers.filter(data => data.created_by === id);
  }

  const getRecName = useMemo(() => (recruiterId: number | string) => {
    const recruiter = recruiters.recruiters.find(r => r.id === recruiterId);
    return recruiter?.name ?? "";
  }, [recruiters.recruiters]);

  const recruiterRows = [
    { key: "calls", label: "Calls" },
    // { key: "screening", label: "Screenings" },
    { key: "registration", label: "Registration" },
    { key: "registration_fro", label: "Registration foreign" },
    { key: "re_registration", label: "Re-registration" },
    { key: "re_registration_fro", label: "Re-registration foreign" },
    { key: "presentation", label: "Presentation" },
    { key: "presentation_fro", label: "Presentation foreign" },
    { key: "interview", label: "Interview" },
    { key: "starter", label: "Starter" },
    { key: "stopper", label: "Stopper" },
    { key: "stopper_permanent", label: "Stopper permanent" }
  ];

  const handleAnalysisFieldChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target as HTMLInputElement;

    setAnalysisData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      type: "officeManager",
      primaryPeriodId: reports.primaryPeriodId ?? null,
      analysis: analysisData
    };

    const url = UPDATE_WEEKLY_REPORTS;
    const response = await ApiCall.service(
      url,
      "POST",
      payload,
      false,
      "central-data-management"
    );
    if (response?.status === 200) {
      CustomNotify({ type: "success", message: response?.msg });
      navigate("/manage-reports");
    } else {
      CustomNotify({ type: "error", message: t("Something went wrong") })
    }
    setLoading(false);
  };

  return (
    <AccessControl
      requiredPermissions={[
        {
          permission: "Reports",
          read: true,
        },
      ]}
      renderNoAccess={true}
    >
      <Title title={`${t("Reporting (weekly office manager)")} - ${targetData?.officeQTarget?.officeName ?? ""} (${reports?.from ?? "-"} to ${reports?.to ?? "-"})`} />
      {loading ? (
        <div style={{ height: "38vw" }} className="d-flex align-items-center justify-content-center">
          <LoadingIcon
            iconType="bars"
            color="#e55496"
            className="m-auto"
            height={"5vw"}
            width={"5vw"}
          />
        </div>

      ) : (
        <>
          <div className="row py-3">
            <div className="col-md-3 mb-3">
              <OfficeTargetTable
                data={targetData?.officeQTarget}
                tableTitle={`Office Q`}
              />
            </div>
            <div className="col-md-3 mb-3">
              <div className="position-relative">
                <div
                  className={`circular-progress-bar-custom recruitment-card-title `}
                  data-label={`€ ${targetData?.officeTarget.reached ?? 0}`}
                >
                  <div>
                    <CircularProgressbarWithChildren
                      value={targetData?.officeTarget?.percentage ?? 0}
                      className="CircularProgressbarWithChildren"
                    >
                      <div className="text-center CircularProgressbar-text text-uppercase">
                        {t("Goal")}
                        <br />
                        <div>{`€ ${targetData?.officeTarget.target ?? 0}`}</div>
                      </div>
                    </CircularProgressbarWithChildren>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-6 mb-3">
              {/* activityLabels: per-locale activity wording from the API, so the rows follow the
                  application language instead of showing the raw English follow-up keys. */}
              <WeeklyReportTable data={reports.weeklyReports} rows={rows} tableTitle={reports?.omName} activityLabels={reports?.activityLabels}/>
            </div>
            {!foreignOffice &&
            <div className="col-md-6">
              <VacanciesTable data={reports.vacancies} tableTitle={t("Vacancies")} />
            </div>
            }
            <div className="col-md-6">
              <VacanciesTable data={reports.foreignVacancies} isFroTable={true} tableTitle={t("Vacancies FRO")} />
            </div>
            <div className="col-md-6">
              <VisitsTable data={reports.visits} tableTitle={t("Visits")} />
            </div>
            <div className="col-md-6">
              <CooAgreementTable
                data={reports.cooAgreement}
                tableTitle={t("Cooperation agreement")}
              />
            </div>
            <div className="col-md-6">
              <ProspectCallsTable
                data={reports.prospectCalls}
                tableTitle={t("Prospect call")}
              />
            </div>
          </div>
          <div className="row pb-5">
            <div className="col-md-2 ">
              <table className="table mb-0 table-bordered position-sticky OMRecriterTableSticky">
                <thead>
                  <tr className="align-middle border-bottom TableHeader">
                    <th className="text-start ps-3">{t("Recruiters")}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    onClick={(e) => { setCurrentRecData({ created_by: "global", reports: {} }) }}
                    key={-1}
                  >
                    <td
                      className={`cursor-pointer ${"global" === currentRecData?.created_by ? "activeRecruiter" : ""}`}
                    >
                      {"Global"}
                    </td>
                  </tr>
                  {recruiters.recruiters.map((recruiter: User, index) => (
                    <tr
                      onClick={(e) => {
                        setCurrentRecruiter(recruiter.id);
                      }}
                      key={index}
                    >
                      <td
                        className={`cursor-pointer ${recruiter.id === currentRecData?.created_by ? "activeRecruiter" : ""}`}
                      >
                        {recruiter.name}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="col-md-10">
              {currentRecData?.created_by === "global" ? (
                <>
                  <TableTitle title={"Global"} />
                  <StartersTable
                    data={reports.starters}
                    view="om"
                    tableTitle={t("Starters")}
                  />
                  <StoppersTable
                    data={reports.stoppers}
                    view="om"
                    tableTitle={t("Stopper")}
                  />
                </>
              ) : (
                <>
                  <TableTitle title={getRecName(currentRecData?.created_by ?? 0)} />
                  <WeeklyReportTable
                    data={currentRecData?.reports as WeeklyReportsData}
                    rows={recruiterRows}
                    activityLabels={reports?.activityLabels}
                  />
                  <StartersTable
                    data={getStarters(currentRecData?.created_by ?? 0)}
                    view="om"
                    tableTitle={t("Starters")}
                  />
                  <StoppersTable
                    data={getStoppers(currentRecData?.created_by ?? 0)}
                    view="om"
                    tableTitle={t("Stopper")}
                  />
                </>
              )}
            </div>
          </div>
          <div className="row">
            <Title title={t("Analysis")} />
            <div className="col-md-4">
              <LabelWithTextAreaField
                name="challenges"
                handleChange={handleAnalysisFieldChange}
                label={t("Commercial or administrative challenges with customers")}
                value={analysisData.challenges}
                isDisabled={authUser.role === OFFICE_MANAGER ? false : true}

              />
            </div>
            <div className="col-md-4">
              <LabelWithTextAreaField
                name="dubi_management"
                handleChange={handleAnalysisFieldChange}
                label={t("Dubi management")}
                value={analysisData.dubi_management}
                isDisabled={authUser.role === OFFICE_MANAGER ? false : true}
              />
            </div>
            <div className="col-md-4">
              <LabelWithTextAreaField
                name="office_management"
                handleChange={handleAnalysisFieldChange}
                label={t("Office management (holidays/ADV/illness)")}
                value={analysisData.office_management}
                isDisabled={authUser.role === OFFICE_MANAGER ? false : true}
              />
            </div>
            <div className="row py-4">
              <div className="col-md-4 align-self-center">
                <Link
                  to="/manage-reports"
                  className=" back-btn text-decoration-underline"
                >
                  {t("Back")}
                </Link>
              </div>
              {/* <AccessControl
                requiredPermissions={[
                  {
                    permission: "Daily turnover",
                    create: true,
                    update: true,
                  },
                ]}
                actions={true}
                strict={false}
              > */}
            {authUser.role !== ADMIN && authUser.role !== SUPER_ADMIN  &&
              <div className="col-md-8">
                {!loading ? (
                  <Button
                    title={t("Save")}
                    type="submit"
                    className="form-button float-end px-3 shadow-none"
                    handleClick={(e) => handleSubmit(e)}
                  />
                ) : (
                  <LoadingIcon
                    iconType="bars"
                    color="#ff4dae"
                    className="float-end"
                    width={"3vw"}
                    height={"3vw"}
                  />
                )}
              </div>
}
              {/* </AccessControl> */}
            </div>
          </div>
        </>
      )}
    </AccessControl>
  );
};

export default WeeklyReports;
